import { useEffect, useState } from "react";
import {
  useGetMe,
  useUpdateMePreferences,
  getGetMeQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Webhook } from "lucide-react";

export function WebhookPreferencesCard() {
  const queryClient = useQueryClient();
  const { data: me } = useGetMe();
  const updateMutation = useUpdateMePreferences();

  const [disabled, setDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (me) {
      setDisabled(me.webhookFailureEmailsDisabled ?? false);
    }
  }, [me]);

  const onToggle = async (next: boolean) => {
    const prev = disabled;
    setDisabled(next);
    try {
      await updateMutation.mutateAsync({
        data: { webhookFailureEmailsDisabled: next },
      });
      queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
      toast.success(
        next
          ? "Webhook failure warning emails are now off."
          : "Webhook failure warning emails are now on.",
      );
    } catch (e) {
      setDisabled(prev);
      toast.error(apiErrorMessage(e, "Failed to save webhook preference"));
    }
  };

  return (
    <Card data-testid="card-webhook-preferences">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Webhook className="w-5 h-5" /> Webhook failure emails
        </CardTitle>
        <CardDescription>
          Controls whether you receive early-warning emails when a webhook
          endpoint starts failing.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Label
              htmlFor="webhook-failure-emails-disabled"
              className="text-sm font-medium cursor-pointer"
            >
              Suppress webhook failure warning emails
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              When on, MailCraft won't send you emails at the 1st and 5th
              consecutive failure. You'll still receive the auto-pause
              notification after {20} consecutive failures.
            </p>
          </div>
          <Switch
            id="webhook-failure-emails-disabled"
            checked={disabled}
            onCheckedChange={onToggle}
            disabled={updateMutation.isPending}
            data-testid="switch-webhook-failure-emails-disabled"
          />
        </div>
      </CardContent>
    </Card>
  );
}
